import React from 'react';
import {
    Navigation,
    Page,
    TaskImageClick,
    Storage,
    TaskMultiSelect,
    Image
} from '@bittnerdevelopmentgroup/interactive';

import bond from './assets/Int_PrinEco5_23-1_Custom.jpg'
import p3a from './assets/PrinEco4_Bonds_p3a.jpg'
import p3b from './assets/PrinEco4_Bonds_p3b.jpg'
import { findByLabelText } from '@testing-library/react';

function Bonds() {
    return <div className="bonds">
        <div className="sr-only" aria-hidden="true">
            {[bond, p3a, p3b].map(src=><img alt="" src={src}/>)}
        </div>
        <Navigation interactiveId="chapter-23">
            <Page>
                <p>
                    Your friend Kara wants to start a web design business and needs a loan to buy computers, software, and other equipment. A bank decides not to lend to her because the new business seems too risky.
                </p>
                <p>
                    Because you know Kara well, you agree to lend her money for one year, with the promise that she will repay the loan plus interest at the end of the year. This arrangement is an example of direct finance and functions like a bond.
                </p>
                <p>
                    <b>In the following bond, select the face value.</b>
                </p>
                <TaskImageClick
                    state={Storage()}
                    hotspots={[
                        {
                        correct: false,
                        label: 'Kara Alexis',
                        feedback: ' ',
                            style: {
                                top: "53px",
                                left: "260px",
                                width: "92px",
                                height: "30px",
                            },
                        },
                        {
                        correct: true,
                        label: '$10,000',
                        feedback: <>This is also known as the <b>par value</b>, and it is the amount due at repayment.</>,
                            style: {
                                top: "53px",
                                left: "410px",
                                width: "78px",
                                height: "30px",
                            },
                        },
                        {
                        correct: false,
                        label: 'February 20, 2024',
                        feedback: " ",
                            style: {
                                top: "126px",
                                left: "354px",
                                width: "145px",
                                height: "30px",
                            },
                        },
                    ]}
                >
                    <Image
                        style={{display: "flex", justifyContent: "center", margin: "auto"}}
                        src={bond}
                        width="90%"
                        alt="A Basic Bond Security certificate."
                        longAlt={"A Basic Bond Security certificate, with the following text: I, Kara Alexis, owe you $10,000. I will pay you on February 20, 2024 (one year from today). At the bottom of the document is Kara Alexis's signature."}
                    />
                </TaskImageClick>
            </Page>
            <Page>
                <p>
                    <b>Now, select the maturity date.</b>
                </p>
                <TaskImageClick
                    state={Storage()}
                    hotspots={[
                        {
                        correct: false,
                        label: 'Kara Alexis',
                        feedback: ' ',
                            style: {
                                top: "53px",
                                left: "260px",
                                width: "92px",
                                height: "30px",
                            },
                        },
                        {
                        correct: false,
                        label: '$10,000',
                        feedback: ' ',
                            style: {
                                top: "53px",
                                left: "410px",
                                width: "78px",
                                height: "30px",
                            },
                        },
                        {
                        correct: true,
                        label: 'February 20, 2024',
                        feedback: <>The <b>maturity date</b> of a bond is the date on which the loan repayment is due.</>,
                            style: {
                                top: "126px",
                                left: "354px",
                                width: "145px",
                                height: "30px",
                            },
                        },
                    ]}
                >
                    <Image
                        style={{display: "flex", justifyContent: "center", margin: "auto"}}
                        src={bond}
                        width="90%"
                        alt="A Basic Bond Security certificate."
                        longAlt={"A Basic Bond Security certificate, with the following text: I, Kara Alexis, owe you $10,000. I will pay you on February 20, 2024 (one year from today). At the bottom of the document is Kara Alexis's signature."}
                    />
                </TaskImageClick>
            </Page>
            <Page>
                <p>
                    You offer to buy Kara’s bond for an <b>initial price</b> (p<sub>0</sub>) of $8,000. Notice that this is different than the $10,000 <b>face value</b> (p<sub>m</sub>) of the bond.
                </p>
                <p>
                    Kara is trying to figure out if this is a good deal, so she decides to calculate the <b>interest rate</b> of the bond using this formula:
                    <div align="center">
                        <Image
                            src={p3a}
                            alt="The interest rate, R, equals the difference face minus initial price, all over the initial price."
                            longAlt={"The interest rate, R, equals the difference face minus initial price, all over the initial price, that is, the difference p sub m minus p sub null, all over p sub null."}
                            width="60%"
                        />
                    </div>
                </p>
                <p>
                    If the price of the bond is $8,000, the interest rate is computed as
                    <div align="center">
                        <Image
                            src={p3b}
                            alt="R equals 25 percent."
                            longAlt={"The interest rate, R, equals the difference p sub m minus p sub null, all over p sub null, which in this case is 10 thousand dollars minus 8 thousand dollars, all over 8 thousand dollars, which equals 25 percent."}
                            width="60%"
                        />
                    </div>
                </p>
                <p>
                    Using these calculations, Kara understands that the interest rate of the bond that you’re offering is 25%.
                </p>
            </Page>
            <Page>
                <p>
                    Now, something changes: you learn that Kara’s car was recently repossessed after she failed to make her loan payments. You still believe in her business, but this makes you more cautious about lending to her.
                </p>
                <p>
                    Your confidence in whether Kara will repay the loan is what economists call <b>default risk</b>: the risk that a borrower will not repay a bond’s face value at maturity.
                </p>
                <TaskMultiSelect
                    state={Storage()}
                    prompt={<>
                        <p>
                            <b>What changes could you make to the bond given this new information?</b> <i>Select all that apply.</i>
                        </p>
                    </>}
                    data={[
                        {
                            label: '$8,000 is a lot of money to risk losing. Maybe I should buy the bond for $7,000 instead.',
                            correct: true,
                        },
                        { 
                            label: "If I’m going to be risking this much money, I want the payout at the end to be worth it. I should increase the interest rate to 30%.",
                            correct: true
                        },
                        {
                            label: "I’ll loan Kara more money. She obviously needs it!",
                            correct: false
                        },
                    ]}
                    correctFeedback="Generally speaking, increases in default risk (1) cause a drop in the price that firms can charge for their bonds and (2) cause an increase in their bonds’ interest rate. Since you know Kara, you can judge her default risk yourself. However, it can be difficult to judge the default risk of all the other firms in an economy. Therefore, private ratings agencies grade the default risk of many other borrowing agencies."
                    incorrectFeedback="When a borrower becomes riskier, lenders typically adjust the terms of the loan to compensate for the higher chance of not being repaid. Think about how the bond’s price or interest rate could change to reflect greater default risk."
                />
            </Page>
        </Navigation>
    </div>
}

export default Bonds